import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { aiApi, athletesApi, statsApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
    Trophy, ArrowLeft, Zap, Brain, Target, TrendingUp,
    Users, Loader2, Sparkles, BarChart3, Award
} from 'lucide-react';
import MaraSportsLogo from '../components/MaraSportsLogo';

const AIPredictionsPage = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [sports, setSports] = useState([]);
    const [athletes, setAthletes] = useState([]);
    const [activeTab, setActiveTab] = useState('talent');
    const [loading, setLoading] = useState(false);
    
    // Talent Detection State
    const [talentFilters, setTalentFilters] = useState({
        sport: 'Football',
        age_category: '',
        country: '',
        metric: 'improvement'
    });
    const [talentResults, setTalentResults] = useState(null);

    // Match Prediction State
    const [predictionFilters, setPredictionFilters] = useState({
        sport: 'Football',
        participant1_id: '',
        participant2_id: ''
    });
    const [predictionResult, setPredictionResult] = useState(null);

    const countries = ['Kenya', 'Ghana', 'Nigeria', 'Rwanda', 'Senegal', 'Zimbabwe'];
    const ageCategories = ['U10', 'U12', 'U15', 'U18', 'U23'];
    const metrics = [
        { value: 'improvement', label: 'Most Improved' },
        { value: 'consistency', label: 'Most Consistent' },
        { value: 'potential', label: 'Highest Potential' }
    ];

    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        if (predictionFilters.sport) {
            loadAthletesBySport(predictionFilters.sport);
        }
    }, [predictionFilters.sport]);

    const loadInitialData = async () => {
        try {
            const [sportsRes, athletesRes] = await Promise.all([
                statsApi.getSports(),
                athletesApi.getAll({ limit: 50 })
            ]);
            setSports(sportsRes.data.sports);
            setAthletes(athletesRes.data);
        } catch (error) {
            console.error('Failed to load data:', error);
        }
    };

    const loadAthletesBySport = async (sport) => {
        try {
            const response = await athletesApi.getAll({ sport, limit: 50 });
            setAthletes(response.data);
        } catch (error) {
            console.error('Failed to load athletes:', error);
        }
    };

    const handleTalentDetection = async () => {
        if (!isAuthenticated) {
            toast.error('Please sign in to use AI features');
            navigate('/auth');
            return;
        }

        setLoading(true);
        setTalentResults(null);
        try {
            const params = {
                sport: talentFilters.sport,
                metric: talentFilters.metric
            };
            if (talentFilters.age_category && talentFilters.age_category !== 'all') {
                params.age_category = talentFilters.age_category;
            }
            if (talentFilters.country && talentFilters.country !== 'all') {
                params.country = talentFilters.country;
            }

            const response = await aiApi.detectTalent(params);
            setTalentResults(response.data);
            toast.success('AI analysis complete!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to analyze talents');
        } finally {
            setLoading(false);
        }
    };

    const handleMatchPrediction = async () => {
        if (!isAuthenticated) {
            toast.error('Please sign in to use AI features');
            navigate('/auth');
            return;
        }

        if (!predictionFilters.participant1_id || !predictionFilters.participant2_id) {
            toast.error('Please select both athletes');
            return;
        }

        if (predictionFilters.participant1_id === predictionFilters.participant2_id) {
            toast.error('Please select different athletes');
            return;
        }

        setLoading(true);
        setPredictionResult(null);
        try {
            const response = await aiApi.predictMatch({
                participant1_id: predictionFilters.participant1_id,
                participant2_id: predictionFilters.participant2_id,
                sport: predictionFilters.sport
            });
            setPredictionResult(response.data);
            toast.success('AI prediction complete!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to generate prediction');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0a1628] text-white">
            {/* Grid overlay */}
            <div className="fixed inset-0 grid-lines pointer-events-none z-0" />

            {/* Header */}
            <header className="sticky top-0 z-30 bg-[#0a1628]/80 backdrop-blur-lg border-b border-white/5">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-4">
                            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-white">
                                <ArrowLeft className="w-4 h-4" />
                            </Link>
                            <Link to="/" className="flex items-center gap-2">
                                <MaraSportsLogo className="h-8" />
                                <span className="font-heading font-bold text-lg tracking-tight uppercase hidden sm:block">
                                    NextGen<span className="text-primary"> Tournaments</span>
                                </span>
                            </Link>
                        </div>
                        <Button
                            onClick={() => navigate('/dashboard')}
                            variant="outline"
                            className="border-white/20 text-sm"
                            data-testid="nav-dashboard"
                        >
                            Dashboard
                        </Button>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative py-16 md:py-24 border-b border-white/5">
                <div className="hero-glow absolute inset-0 z-0" />
                <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="flex items-center gap-2 mb-4">
                        <Sparkles className="w-4 h-4 text-secondary" />
                        <p className="text-xs tracking-widest uppercase text-secondary">Powered by AI</p>
                    </div>
                    <h1 className="font-heading font-black text-4xl md:text-6xl tracking-tighter uppercase mb-4">
                        AI Predictions
                    </h1>
                    <p className="text-muted-foreground text-lg max-w-xl">
                        Advanced AI-powered talent detection and match predictions. Discover rising stars and get data-driven insights.
                    </p>
                </div>
            </section>

            {/* AI Tools */}
            <section className="py-12">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
                        <TabsList className="bg-[#0d2847] border border-white/5 p-1 w-full md:w-auto">
                            <TabsTrigger 
                                value="talent" 
                                className="data-[state=active]:bg-secondary data-[state=active]:text-white px-6"
                                data-testid="tab-talent"
                            >
                                <Brain className="w-4 h-4 mr-2" />
                                Talent Detection
                            </TabsTrigger>
                            <TabsTrigger 
                                value="predictions" 
                                className="data-[state=active]:bg-secondary data-[state=active]:text-white px-6"
                                data-testid="tab-predictions"
                            >
                                <Target className="w-4 h-4 mr-2" />
                                Match Predictions
                            </TabsTrigger>
                        </TabsList>

                        {/* Talent Detection Tab */}
                        <TabsContent value="talent" className="space-y-8">
                            <div className="bg-[#0d2847] border border-white/5 p-6 md:p-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-10 h-10 bg-secondary/10 flex items-center justify-center">
                                        <Brain className="w-5 h-5 text-secondary" />
                                    </div>
                                    <div>
                                        <h2 className="font-heading font-bold text-xl uppercase tracking-tight">
                                            AI Talent Detection
                                        </h2>
                                        <p className="text-sm text-muted-foreground">Find rising stars based on performance data</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                                    <Select
                                        value={talentFilters.sport}
                                        onValueChange={(value) => setTalentFilters({ ...talentFilters, sport: value })}
                                    >
                                        <SelectTrigger className="bg-[#0d2847] border-white/10 h-11" data-testid="talent-sport">
                                            <SelectValue placeholder="Select Sport" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#0d2847] border-white/10">
                                            {sports.map((sport) => (
                                                <SelectItem key={sport} value={sport}>{sport}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>

                                    <Select
                                        value={talentFilters.age_category}
                                        onValueChange={(value) => setTalentFilters({ ...talentFilters, age_category: value })}
                                    >
                                        <SelectTrigger className="bg-[#0d2847] border-white/10 h-11" data-testid="talent-age">
                                            <SelectValue placeholder="Age Category" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#0d2847] border-white/10">
                                            <SelectItem value="all">All Ages</SelectItem>
                                            {ageCategories.map((cat) => (
                                                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>

                                    <Select
                                        value={talentFilters.country}
                                        onValueChange={(value) => setTalentFilters({ ...talentFilters, country: value })}
                                    >
                                        <SelectTrigger className="bg-[#0d2847] border-white/10 h-11" data-testid="talent-country">
                                            <SelectValue placeholder="Country" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#0d2847] border-white/10">
                                            <SelectItem value="all">All Countries</SelectItem>
                                            {countries.map((country) => (
                                                <SelectItem key={country} value={country}>{country}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>

                                    <Select
                                        value={talentFilters.metric}
                                        onValueChange={(value) => setTalentFilters({ ...talentFilters, metric: value })}
                                    >
                                        <SelectTrigger className="bg-[#0d2847] border-white/10 h-11" data-testid="talent-metric">
                                            <SelectValue placeholder="Metric" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#0d2847] border-white/10">
                                            {metrics.map((metric) => (
                                                <SelectItem key={metric.value} value={metric.value}>{metric.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <Button
                                    onClick={handleTalentDetection}
                                    disabled={loading}
                                    className="bg-secondary hover:bg-blue-600 text-white uppercase tracking-widest text-sm font-bold h-11 px-8"
                                    data-testid="analyze-talent-btn"
                                >
                                    {loading ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    ) : (
                                        <Zap className="w-4 h-4 mr-2" />
                                    )}
                                    Analyze Talent
                                </Button>
                            </div>

                            {/* Talent Results */}
                            {talentResults && (
                                <div className="space-y-6">
                                    {/* Top Talents */}
                                    {talentResults.top_talents?.length > 0 && (
                                        <div className="bg-[#0d2847] border border-white/5 p-6 md:p-8">
                                            <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-6 flex items-center gap-2">
                                                <Award className="w-5 h-5 text-accent" />
                                                Top Talents Identified
                                            </h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {talentResults.top_talents.map((talent, index) => (
                                                    <div key={talent.id || index} className="bg-[#0a1628] border border-white/5 p-4">
                                                        <div className="flex items-center gap-3 mb-3">
                                                            <span className="stat-display text-2xl text-accent">{index + 1}</span>
                                                            <div>
                                                                <p className="font-medium">{talent.name}</p>
                                                                <p className="text-xs text-muted-foreground">Score: {talent.score}</p>
                                                            </div>
                                                        </div>
                                                        <p className="text-sm text-muted-foreground">{talent.reason}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Insights */}
                                    {talentResults.insights && (
                                        <div className="bg-[#0d2847] border border-white/5 p-6 md:p-8">
                                            <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-4 flex items-center gap-2">
                                                <BarChart3 className="w-5 h-5 text-secondary" />
                                                AI Insights
                                            </h3>
                                            <p className="text-muted-foreground whitespace-pre-line">{talentResults.insights}</p>
                                        </div>
                                    )}

                                    {/* Recommendations */}
                                    {talentResults.recommendations && (
                                        <div className="bg-[#0d2847] border border-white/5 p-6 md:p-8">
                                            <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-4 flex items-center gap-2">
                                                <TrendingUp className="w-5 h-5 text-success" />
                                                Recommendations
                                            </h3>
                                            <p className="text-muted-foreground whitespace-pre-line">{talentResults.recommendations}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </TabsContent>

                        {/* Match Predictions Tab */}
                        <TabsContent value="predictions" className="space-y-8">
                            <div className="bg-[#0d2847] border border-white/5 p-6 md:p-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-10 h-10 bg-primary/10 flex items-center justify-center">
                                        <Target className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <h2 className="font-heading font-bold text-xl uppercase tracking-tight">
                                            AI Match Predictions
                                        </h2>
                                        <p className="text-sm text-muted-foreground">Get win probability and key factors</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                    <Select
                                        value={predictionFilters.sport}
                                        onValueChange={(value) => setPredictionFilters({ ...predictionFilters, sport: value, participant1_id: '', participant2_id: '' })}
                                    >
                                        <SelectTrigger className="bg-[#0d2847] border-white/10 h-11" data-testid="predict-sport">
                                            <SelectValue placeholder="Select Sport" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#0d2847] border-white/10">
                                            {sports.map((sport) => (
                                                <SelectItem key={sport} value={sport}>{sport}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>

                                    <Select
                                        value={predictionFilters.participant1_id}
                                        onValueChange={(value) => setPredictionFilters({ ...predictionFilters, participant1_id: value })}
                                    >
                                        <SelectTrigger className="bg-[#0d2847] border-white/10 h-11" data-testid="predict-athlete1">
                                            <SelectValue placeholder="Athlete 1" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#0d2847] border-white/10 max-h-60">
                                            {athletes.filter(a => a.sports?.includes(predictionFilters.sport)).map((athlete) => (
                                                <SelectItem key={athlete.id} value={athlete.id}>{athlete.full_name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>

                                    <Select
                                        value={predictionFilters.participant2_id}
                                        onValueChange={(value) => setPredictionFilters({ ...predictionFilters, participant2_id: value })}
                                    >
                                        <SelectTrigger className="bg-[#0d2847] border-white/10 h-11" data-testid="predict-athlete2">
                                            <SelectValue placeholder="Athlete 2" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#0d2847] border-white/10 max-h-60">
                                            {athletes.filter(a => a.sports?.includes(predictionFilters.sport) && a.id !== predictionFilters.participant1_id).map((athlete) => (
                                                <SelectItem key={athlete.id} value={athlete.id}>{athlete.full_name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <Button
                                    onClick={handleMatchPrediction}
                                    disabled={loading || !predictionFilters.participant1_id || !predictionFilters.participant2_id}
                                    className="bg-primary hover:bg-red-600 text-white uppercase tracking-widest text-sm font-bold h-11 px-8"
                                    data-testid="predict-match-btn"
                                >
                                    {loading ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    ) : (
                                        <Target className="w-4 h-4 mr-2" />
                                    )}
                                    Predict Match
                                </Button>
                            </div>

                            {/* Prediction Results */}
                            {predictionResult && (
                                <div className="bg-[#0d2847] border border-white/5 p-6 md:p-8">
                                    <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-6">
                                        Match Prediction
                                    </h3>

                                    {/* VS Display */}
                                    <div className="grid grid-cols-3 gap-4 mb-8">
                                        <div className="text-center">
                                            <div className="w-20 h-20 bg-primary/10 flex items-center justify-center mx-auto mb-3">
                                                <Users className="w-10 h-10 text-primary" />
                                            </div>
                                            <p className="font-medium">{predictionResult.participant1?.name}</p>
                                            <p className="stat-display text-4xl text-primary mt-2">
                                                {predictionResult.prediction?.participant1_win_probability}%
                                            </p>
                                        </div>
                                        <div className="flex items-center justify-center">
                                            <span className="font-heading font-black text-4xl text-muted-foreground">VS</span>
                                        </div>
                                        <div className="text-center">
                                            <div className="w-20 h-20 bg-secondary/10 flex items-center justify-center mx-auto mb-3">
                                                <Users className="w-10 h-10 text-secondary" />
                                            </div>
                                            <p className="font-medium">{predictionResult.participant2?.name}</p>
                                            <p className="stat-display text-4xl text-secondary mt-2">
                                                {predictionResult.prediction?.participant2_win_probability}%
                                            </p>
                                        </div>
                                    </div>

                                    {/* Key Factors */}
                                    {predictionResult.key_factors?.length > 0 && (
                                        <div className="mb-6">
                                            <h4 className="text-sm tracking-widest uppercase text-muted-foreground mb-3">Key Factors</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {predictionResult.key_factors.map((factor, index) => (
                                                    <span key={index} className="px-3 py-1 bg-white/5 text-sm">
                                                        {factor}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Predicted Score */}
                                    {predictionResult.predicted_score && (
                                        <div className="mb-6">
                                            <h4 className="text-sm tracking-widest uppercase text-muted-foreground mb-2">Predicted Score</h4>
                                            <p className="text-lg">{predictionResult.predicted_score}</p>
                                        </div>
                                    )}

                                    {/* Analysis */}
                                    {predictionResult.analysis && (
                                        <div>
                                            <h4 className="text-sm tracking-widest uppercase text-muted-foreground mb-2">Analysis</h4>
                                            <p className="text-muted-foreground whitespace-pre-line">{predictionResult.analysis}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </TabsContent>
                    </Tabs>
                </div>
            </section>
        </div>
    );
};

export default AIPredictionsPage;
